import { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import Modal from '../common/Modal';
import tenantService, { type Tenant } from '../../services/tenant.service';

interface TenantAssignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (tenantId: string, internetBreakout: boolean, tunnelProtocol?: 'wireguard' | 'ikev2') => void | Promise<void>;
  existingTenantIds: string[];
}

const TenantAssignModal = ({ isOpen, onClose, onAssign, existingTenantIds }: TenantAssignModalProps) => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [internetBreakout, setInternetBreakout] = useState(true);
  const [tunnelProtocol, setTunnelProtocol] = useState<'wireguard' | 'ikev2'>('wireguard');
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setInternetBreakout(true);
      setTunnelProtocol('wireguard');
      setSelectedTenant('');
      loadTenants();
    }
  }, [isOpen]);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const response = await tenantService.list({ pageSize: 100 });
      // Filter out already-assigned tenants
      const available = response.items.filter(
        (t: Tenant) => !existingTenantIds.includes(t.id)
      );
      setTenants(available);
      if (available.length > 0) {
        setSelectedTenant(available[0].id);
      }
    } catch {
      // Silently handle - dropdown will show "No available tenants"
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedTenant) return;
    try {
      setAssigning(true);
      await onAssign(selectedTenant, internetBreakout, tunnelProtocol);
      onClose();
    } catch {
      // Error handled by parent
    } finally {
      setAssigning(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary">Cancel</button>
      <button
        onClick={handleAssign}
        disabled={assigning || !selectedTenant || tenants.length === 0}
        className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
      >
        <Users className="w-4 h-4" />
        <span>{assigning ? 'Assigning...' : 'Assign Tenant'}</span>
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Tenant to Shared Gateway"
      size="md"
      footer={footer}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tenant <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            className="input-base w-full"
            disabled={loading || tenants.length === 0}
          >
            {loading ? (
              <option>Loading tenants...</option>
            ) : tenants.length === 0 ? (
              <option>No available tenants</option>
            ) : (
              tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))
            )}
          </select>
          <p className="mt-1 text-sm text-gray-500">
            Select a tenant to assign. Each tenant gets an isolated VRF with dedicated tunnel ports.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">App VPN Protocol</label>
          <div className="flex space-x-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="tunnelProtocol"
                value="wireguard"
                checked={tunnelProtocol === 'wireguard'}
                onChange={() => setTunnelProtocol('wireguard')}
                className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Tunnel (WireGuard)</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="tunnelProtocol"
                value="ikev2"
                checked={tunnelProtocol === 'ikev2'}
                onChange={() => setTunnelProtocol('ikev2')}
                className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">VPN (IKEv2)</span>
            </label>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {tunnelProtocol === 'ikev2'
              ? 'Certificate-based IKEv2 via StrongSwan. Supports native OS VPN clients.'
              : 'WireGuard tunnel with X25519 keypairs. Requires WireGuard client app.'}
          </p>
        </div>

        <div>
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={internetBreakout}
              onChange={(e) => setInternetBreakout(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <span className="block text-sm font-medium text-gray-700">Enable Internet Breakout</span>
              <span className="block text-sm text-gray-500">
                Allow this tenant's traffic to reach the internet via the shared WAN interface (SNAT).
                When disabled, traffic is only routable via E2E peering tunnels.
              </span>
            </div>
          </label>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">What happens on assignment</h4>
          <ul className="list-disc list-inside space-y-1 text-sm text-blue-700">
            <li>A VRF is allocated for tenant isolation</li>
            <li>3 tunnel interfaces are provisioned (IoT, Apps, Peering)</li>
            {tunnelProtocol === 'ikev2'
              ? <li>IKEv2 server certificate generated and StrongSwan configured</li>
              : <li>WireGuard App VPN keypair generated</li>}
            {internetBreakout && <li>NAT and inter-VRF routing configured for internet access</li>}
            <li>The tenant onboard message is sent to the device</li>
          </ul>
        </div>
      </div>
    </Modal>
  );
};

export default TenantAssignModal;
